import { useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface DocumentLinkProps {
  bucket: string;
  filePath: string;
  children: ReactNode;
  className?: string;
  target?: string;
  rel?: string;
}

export const DocumentLink = ({ 
  bucket, 
  filePath, 
  children, 
  className = '',
  target = '_blank',
  rel = 'noopener noreferrer'
}: DocumentLinkProps) => {
  const [signedUrl, setSignedUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const isExternalUrl = filePath.startsWith('http://') || filePath.startsWith('https://');

  useEffect(() => {
    // If it's an external URL, use it directly
    if (isExternalUrl) {
      setSignedUrl(filePath);
      setLoading(false);
      return;
    }

    // Otherwise, get signed URL from storage
    const getSignedUrl = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase.storage
          .from(bucket)
          .createSignedUrl(filePath, 3600); // 1 hour expiry
        
        if (error) throw error;
        setSignedUrl(data?.signedUrl || '');
      } catch (error) {
        console.error('[INTERNAL] Error getting signed URL:', error);
        setSignedUrl('');
      } finally {
        setLoading(false);
      }
    };

    getSignedUrl();
  }, [bucket, filePath, isExternalUrl]);

  if (loading) {
    return (
      <div className={className}>
        {children}
      </div>
    );
  }

  if (!signedUrl) {
    return (
      <div className={className + ' opacity-50 cursor-not-allowed'}>
        {children}
      </div>
    );
  }

  return (
    <a 
      href={signedUrl} 
      target={target} 
      rel={rel}
      className={className}
    >
      {children}
    </a>
  );
};
